import { useQuery } from 'react-query';
import { Axios } from '@utils/api/axios-instance.ts';
import { GET_URL_NEW } from '@utils/api/endpoints.ts';
import { useNewAppSearchParams } from '@utils/hooks/use-new-app-search-params.ts';
import type {
  SessionProgressItem,
  SessionProgressResponse
} from '@utils/types/stt-sessions/stt-sessions.ts';

const ACTIVE_STATES = new Set<SessionProgressItem['progress_state']>([
  'queued',
  'running'
]);
const POLL_MS = 5_000;

export const useGetSessionProgress = () => {
  const { searchParams } = useNewAppSearchParams();

  const offset = Number.isFinite(Number(searchParams.offset))
    ? Number(searchParams.offset)
    : 0;
  const limit = Number.isFinite(Number(searchParams.limit))
    ? Number(searchParams.limit)
    : 25;
  const sortField = (searchParams.sort as string | undefined) || undefined;
  const sortDirection =
    (searchParams.direction as 'asc' | 'desc' | undefined) || undefined;
  const qString = (searchParams.q as string | undefined) || undefined;

  return useQuery<Map<string, SessionProgressItem>>(
    ['sessionProgress', offset, limit, qString, sortField, sortDirection],
    async () => {
      const { sttSessionsProgress } = GET_URL_NEW({
        offset,
        limit,
        q: qString,
        sort: sortField,
        direction: sortDirection
      });
      const { data } =
        await Axios.get<SessionProgressResponse>(sttSessionsProgress);
      const map = new Map<string, SessionProgressItem>();
      (data.result ?? []).forEach(item => map.set(item.zoom_id, item));
      return map;
    },
    {
      staleTime: POLL_MS,
      refetchInterval: data => {
        if (!data) return POLL_MS;
        const hasActive = [...data.values()].some(item =>
          ACTIVE_STATES.has(item.progress_state)
        );
        return hasActive ? POLL_MS : false;
      },
      keepPreviousData: true,
      useErrorBoundary: false,
      retry: false
    }
  );
};
